// ============================================================================
// Integration Settings Page — Overview (Level 1)
// ============================================================================
// Summary page showing all integration connections as clickable cards.
// Click a card to navigate to the detail page.

import { useState } from 'react'
import { Plus, Unplug } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useIntegrationOverview } from '@/hooks/useIntegrations'
import { IntegrationProviderCard } from '@/components/integrations/IntegrationProviderCard'
import { SyncLogViewer } from '@/components/integrations/SyncLogViewer'
import { ConnectionWizard } from '@/components/integrations/ConnectionWizard'

export function IntegrationSettingsPage() {
  const [wizardOpen, setWizardOpen] = useState(false)
  const { data: overviewData, isLoading } = useIntegrationOverview()

  const hasConnections = overviewData && overviewData.length > 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground">
            Connect external reservation and tracking systems.
          </p>
        </div>
        <Button onClick={() => setWizardOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Connection
        </Button>
      </div>

      <Tabs defaultValue="connections">
        <TabsList>
          <TabsTrigger value="connections">Connections</TabsTrigger>
          <TabsTrigger value="log">Sync Log</TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="mt-4">
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-[120px]" />
              ))}
            </div>
          ) : !hasConnections ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Unplug className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No integrations connected</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                Connect an external system like FlightBridge to automatically receive
                flight reservations and sync aircraft data.
              </p>
              <Button onClick={() => setWizardOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Connection
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {overviewData.map((overview) => (
                <IntegrationProviderCard
                  key={overview.id}
                  overview={overview}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <SyncLogViewer />
        </TabsContent>
      </Tabs>

      <ConnectionWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  )
}
